import { Inject } from '@odinjs/odin';

import { APPLICATION_ROUTES, Endpoint, IeAnexo, Report, ReportService, Route, setPredefinedFiles, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import { HpmsGuiaAnaliseApi } from '@philips-emr/hpms-stipulator-components';
import { HPMSOnInformarParecerClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { ActionAPI, BaseFileUploaderAPI, BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues, BasicFileProps, FetchAPI, FieldAPI, FormAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import Controller, { OnAfterFetch, OnBeforeFetch, OnCancel, OnChange, OnClick, OnClose, OnMount, OnSelect, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, ClickEvent, FetchEvent, MountEvent, SelectEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HttpService, MessageService, ModalService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { isEmptyArrayOrObject, noop } from '@philips-emr/tws-utils';

import { SolicitacoesPendentesDetalhesAliases as Aliases, FragmentNames } from '../../codegen';
import { Anexo } from '../../interfaces/anexo';
import BaseController from '../BaseController';

interface Base {
  ANEXOS: string;
  DADOS: string;
  DOWNLOAD: IeAnexo | IeAnexo[];
  ITENS: string;
  PARAMETROS: {};
  SUBMIT: string;
}

@Controller({ fragment: FragmentNames.SOLICITACOES_PENDENTES_DETALHES })
export default class SolicitacoesPendentesDetalhesController extends BaseController {

  private anexos: BasicFileProps[] = [];
  private reportService: ReportService;
  private regraAnexos: Anexo[];

  @Inject(Aliases.HPMS_GUIA_ANALISE_ITENS)
  private hpmsGuiaAnaliseItens: HpmsGuiaAnaliseApi;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toastNotification: ToastNotificationAPI;
  @Inject(Aliases.ACTION_PARECER)
  private actionParecer: ActionAPI;
  @Inject(Aliases.ACTION_ALTERAR_DATA_VALIDADE)
  private actionAlterarDataValidade: ActionAPI;
  @Inject(Aliases.ACTION_ASSUMIR_ANALISE)
  private actionAssumirAnalise: ActionAPI;
  @Inject(Aliases.ACTION_PESQUISA_UTILIZACAO)
  private actionPesquisaUtilizacao: ActionAPI;
  @Inject(Aliases.ACTION_ADD_FILE)
  private actionAddFile: ActionAPI;
  @Inject(Aliases.PANEL_ANEXOS)
  private panelAnexos: PanelAPI;
  @Inject(Aliases.FIELD_GUIA)
  private fieldGuia: FieldAPI;
  @Inject(Aliases.FIELD_REQUISICAO)
  private fieldRequisicao: FieldAPI;
  @Inject(Aliases.FIELD_OBSERVACAO)
  private fieldObservacao: FieldAPI;
  @Inject(Aliases.PANEL_FILE_UPLOADER_MODAL)
  private panelFileUploaderModal: PanelAPI;
  @Inject(Aliases.FORM_DADOS)
  private formDados: FormAPI;
  @Inject(Aliases.FETCH_DADOS)
  private fetchDados: FetchAPI;
  @Inject(Aliases.ACTION_PRINT)
  private print: ActionAPI;

  @OnMount(Aliases.LAYOUT_SOLICITACOES_PENDENTES_DETALHES)
  onMount(): void {
    const { nrSeqContrato, nrSeqGuia, nrSeqAuditoria } = this.historyService.getCurrentRouteParams();

    if (!nrSeqGuia) {
      void HttpService.put(Endpoint.PUT.REQUISICAO_VISUALIZADA, { nrSeqContrato, nrSeqAuditoria });
    }
  }

  @OnMount(Aliases.ACTION_ADD_FILE)
  onMountActionAddFile(event: MountEvent): void {
    const api = event.target as ActionAPI;
    const { nrSeqGuia } = this.historyService.getCurrentRouteParams();
    const { path } = this.historyService.getMatch();

    if (!nrSeqGuia && path != Route.HISTORICO_SOLICITACOES_ATENDIMENTO_DETALHES) {
      void setPredefinedFiles(18, null).then(response => response && api.setEnabled(!!response.length));
    }
  }

  @OnMount(Aliases.FILE_UPLOADER_MODAL)
  onMountFileUploaderModal(event: MountEvent): void {
    const api = event.target as BaseFileUploaderAPI<BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues>;
    void setPredefinedFiles(18, api).then(anexos => this.regraAnexos = anexos as unknown as Anexo[]);
    this.anexos.length > 0 && api.setValue(this.anexos);
  }

  @OnChange(Aliases.FILE_UPLOADER_MODAL)
  onChangeFileUploaderModal(event: ChangeEvent<{ filename: string, filepath: string, size: number }[]>): void {
    const api = event.target as unknown as BaseFileUploaderAPI<BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues>;
    api.setValue(event.value);
  }

  @OnClose(Aliases.FILE_UPLOADER_MODAL)
  onCloseFileUploaderModal(event: CloseEvent): void {
    const api = event.target as unknown as BaseFileUploaderAPI<BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues>;
    this.panelFileUploaderModal.setVisible(false);

    const files = api.getValue();
    if (!files || isEmptyArrayOrObject(files)) {
      this.anexos = [];
    } else {
      this.anexos = files;
    }
  }

  @OnBeforeFetch(Aliases.FETCH_DADOS)
  onBeforeFetchDados(event: MountEvent): void {
    const fetch = event.target as unknown as FetchAPI;
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && fetch.setParameters({ ...fetch.getParameters(), nrSeqSubestipulante });
  }

  @OnMount(Aliases.FETCH_DADOS)
  onMountFetchDados(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { DADOS } = this.getBase();

    api.setEndpoint(DADOS);
    api.forceFetch();
  }

  @OnMount(Aliases.FETCH_ANEXOS)
  onMountFetchAnexos(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { ANEXOS } = this.getBase();

    api.setEndpoint(ANEXOS);
    api.forceFetch();
  }

  @OnMount(Aliases.FETCH_ITENS)
  onMountFetchItens(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { ITENS } = this.getBase();

    api.setEndpoint(ITENS);
    api.forceFetch();
  }

  @OnMount(Aliases.FORM_DADOS)
  onMountFormDados(): void {
    const { nrSeqGuia } = this.historyService.getCurrentRouteParams();
    this.fieldGuia.setVisible(!!nrSeqGuia);
    this.fieldRequisicao.setVisible(!nrSeqGuia);
  }

  @OnMount(Aliases.FORM_ITENS)
  onMountFormItens(event: MountEvent): void {
    const api = event.target as FormAPI;
    const { PARAMETROS } = this.getBase();
    const { path } = this.historyService.getMatch();
    const { nrSeqGuia } = this.historyService.getCurrentRouteParams();

    this.actionPesquisaUtilizacao.setEnabled(!nrSeqGuia);

    if (path == Route.HISTORICO_SOLICITACOES_ATENDIMENTO_DETALHES) {
      api.setEditing(false);
      api.setDisabled(true);

      this.fieldObservacao.setVisible(false);
      this.actionAddFile.setEnabled(false);
      this.hpmsGuiaAnaliseItens.setHiddenContextMenu();

      void HttpService.get(Endpoint.GET.HISTORICO_ATENDIMENTOS_OBTER_ANALISE, PARAMETROS).then(() => this.actionAssumirAnalise.setEnabled(true));
      void HttpService.get(Endpoint.GET.HISTORICO_ATENDIMENTOS_OBTER_DATA_VALIDADE, PARAMETROS).then(() => this.actionAlterarDataValidade.setEnabled(true));
    }
  }

  @OnSelect(Aliases.ATTACHMENTS_LIST)
  onSelectAttachment(event: SelectEvent<Anexo>): void {
    const { DOWNLOAD } = this.getBase();    
    const { value } = event;
    const { nrSeqAnexo, filename, nrSeqTipoDocumento } = value;
    const ieTabela = Array.isArray(DOWNLOAD) ? DOWNLOAD[nrSeqTipoDocumento as unknown as number] : DOWNLOAD;
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
 
    FileService.download(Endpoint.GET.SOLIC_PENDENTE_ANEXO_DOWNLOAD, filename, { ieTabela, nrSeqAnexo, nrSeqContrato }, HttpMethod.GET)
      .catch(() => ToastServiceStipulator.error(MessageService.translate('stipulator.arquivo-nao-localizado')));
  }

  @OnMount(Aliases.ACTION_PARECER)
  onMountActionParecer(event: MountEvent): void {
    const { nrSeqGuia } = this.historyService.getCurrentRouteParams();
    const { PARAMETROS } = this.getBase();
    const api = event.target as ActionAPI;

    if (nrSeqGuia) {
      void HttpService.get<[]>(Endpoint.GET.GUIA_PARECER, PARAMETROS)
        .then((response) => response.body && !response.body ?.length && api.setEnabled(false));
    }
  }

  @OnClick(Aliases.ACTION_PARECER)
  onClickActionParecer(): void {
    const { nrSeqContrato, nrSeqGuia } = this.historyService.getCurrentRouteParams();

    ModalService.openDefault({
      closeable: true,
      onClose: noop,
      onSubmit: noop,
      fragment: 'solicitacoes-pendentes-detalhes-parecer',
      title: 'stipulator.parecer',
      contextData: { nrSeqContrato, nrSeqGuia },
    });
  }

  @OnClick(Aliases.ACTION_PESQUISA_UTILIZACAO)
  onClickActionPesquisaUtilizacao(): void {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const { nrSeqBeneficiario } = this.formDados.getRecord();

    ModalService.openDefault({
      closeable: true,
      onClose: noop,
      onSubmit: noop,
      fragment: 'pesquisa-utilizacao',
      title: 'stipulator.pesquisa-utilizacao',
      contextData: { nrSeqContrato, nrSeqBeneficiario },
    });
  }

  @OnClick(Aliases.ACTION_ALTERAR_DATA_VALIDADE)
  onClickActionAlterarDataValidade(): void {
    const { nrSeqContrato, nrSeqAuditoria } = this.historyService.getCurrentRouteParams();

    ModalService.openDefault({
      closeable: true,
      onClose: noop,
      onSubmit: () => this.fetchDados.forceFetch(),
      onCancel: noop,
      fragment: 'alterar-data-validade',
      title: 'stipulator.alterar-data-validade',
      contextData: { nrSeqContrato, nrSeqAuditoria },
    });
  }

  @OnClick(Aliases.ACTION_ASSUMIR_ANALISE)
  onClickActionAssumirAnalise(): void {
    const { PARAMETROS } = this.getBase();

    void HttpService.put<string>(Endpoint.PUT.HISTORICO_ATENDIMENTOS_ASSUMIR_ANALISE, PARAMETROS)
      .then(responde => {
        if (responde && responde.body) {
          ToastServiceStipulator.success(MessageService.translate(responde.body));
          this.actionAssumirAnalise.setEnabled(false);
        }
      })
      .catch(ex => ex && ex.body && ToastServiceStipulator.warning(MessageService.translate(ex.body)));
  }

  @OnClick(Aliases.ACTION_ADD_FILE)
  onClickActionAddFile(): void {
    this.panelFileUploaderModal.setVisible(true);
  }

  @HPMSOnInformarParecerClick(Aliases.HPMS_GUIA_ANALISE_ITENS)
  onEventListenerInformarParecerClick(event: ClickEvent): void {
    const { nrSeqContrato, nrSeqAuditoria } = this.historyService.getCurrentRouteParams();

    ModalService.openDefault({
      closeable: true,
      onClose: noop,
      onSubmit: event => this.hpmsGuiaAnaliseItens.setSituacao(event.record),
      onCancel: noop,
      fragment: 'solicitacoes-pendentes-informar-parecer',
      title: 'stipulator.parecer',
      contextData: { nrSeqContrato, nrSeqAuditoria, data: event.value },
    });
  }

  @OnSubmit(Aliases.FORM_ITENS)
  onSubmit(event: SubmitEvent): void {
    const { PARAMETROS, SUBMIT } = this.getBase();
    const { record, target } = event;
    const api = target as FormAPI;
    const data = this.hpmsGuiaAnaliseItens.getRecord();
    const anexos = this.handleAnexos(this.anexos);
    const body = { ...record, ...(data && { itens: data }), anexos: anexos };
    const { itens } = body;
    const newItens: object[] = [];

    if (itens) {
      Object.values(itens).forEach(value => newItens.push(value as {}));
      body.itens = newItens;
    }

    this.toastNotification.setVisible(false);
    api.setDisabled(true);

    void HttpService.put<string>(SUBMIT, PARAMETROS, { body })
      .then(response => {
        response.body && ToastServiceStipulator.success(MessageService.translate(response.body));
        this.historyService.push(APPLICATION_ROUTES.ANALISE_ATENDIMENTOS);
      }).catch(ex => {
        api.setDisabled(false);

        if (ex.body instanceof Object) {
          ToastServiceStipulator.error(MessageService.translate(ex.body[Object.keys(ex.body)[0]]));
        } else {
          this.toastNotification.setMessage(MessageService.translate(ex.body));
          this.toastNotification.setVisible(true);
        }
      });
  }

  @OnAfterFetch(Aliases.FETCH_ANEXOS)
  onAfterFetchAnexos(event: FetchEvent): void {
    if (!event.data) {
      return;
    }
    const data = event.data as [];

    if (data.length == 0) {
      this.panelAnexos.setVisible(false);
    }
  }

  @OnAfterFetch(Aliases.FETCH_ITENS)
  onAfterFetchItens(event: FetchEvent): void {
    if (!event.data) {
      return;
    }
    const { nrSeqGuia } = this.historyService.getCurrentRouteParams();

    if (!nrSeqGuia) {
      this.hpmsGuiaAnaliseItens.setReadonly();
      this.actionParecer.setEnabled(false);
    }
  }

  @OnCancel(Aliases.FORM_ITENS)
  onCancel(): void {
    this.historyService.push(APPLICATION_ROUTES.ANALISE_ATENDIMENTOS);
  }

  @OnMount(Aliases.ACTION_PRINT)
  onMountAction(event: MountEvent): void {

    const api = event.target as ActionAPI;

    const { path } = this.historyService.getMatch();

    if (path == Route.SOLICITACOES_PENDENTES_DETALHES_REQUISICAO) {
      api.setEnabled(false);
      
    }

    this.reportService = new ReportService('12', Endpoint.GET.RELATORIOS_POR_GRUPO);
    void this.reportService.getRelatoriosByCdGrupo()
      .then((response: Report[]) => {
        if (response.length === 0) {
          this.print.setEnabled(false);
        }
      });
  }

  @OnClick(Aliases.ACTION_PRINT)
  onClickPrint(): void {
    const record = this.formDados.getRecord();
    this.reportService && this.reportService.abreListaRelatorios({ NR_SEQ_REQUISICAO: record.nrSeqRequisicao, NR_SEQUENCIA_AUTOR: record.nrSeqRequisicao });
  }

  private getBase(): Base {
    const { nrSeqContrato, nrSeqGuia, nrSeqAuditoria } = this.historyService.getCurrentRouteParams();

    this.context.set({ ...(nrSeqGuia && { nrSeqGuia }), ...(nrSeqAuditoria && { nrSeqAuditoria }) });

    return nrSeqGuia ? {
      ANEXOS: Endpoint.GET.GUIA_ANEXOS,
      DADOS: Endpoint.GET.GUIA_DADOS,
      DOWNLOAD: IeAnexo.GUIA,
      ITENS: Endpoint.GET.GUIA_ITENS,
      PARAMETROS: { nrSeqContrato, nrSeqGuia },
      SUBMIT: Endpoint.PUT.GUIA_SOLICITACOES_PENDENTES_FINALIZAR,
    } : {
      ANEXOS: Endpoint.GET.REQUISICAO_ANEXOS,
      DADOS: Endpoint.GET.REQUISICAO_DADOS,
      DOWNLOAD: [IeAnexo.REQUISICAO_AUDITORIA, IeAnexo.REQUISICAO],
      ITENS: Endpoint.GET.REQUISICAO_ITENS,
      PARAMETROS: { nrSeqContrato, nrSeqAuditoria },
      SUBMIT: Endpoint.PUT.REQUISICAO_SOLICITACOES_PENDENTES_FINALIZAR,
    };
  }

  private handleAnexos(anexos: BasicFileProps[]): Anexo[] {
    const handledAnexos = anexos?.map(anexo => {
      const handledAnexo = anexo as unknown as Anexo;
      const predefinedFileKey = anexo.predefinedFileKey ?? '';
      const nrSeqTipoDocumento = this.regraAnexos.find(regraAnexo => +regraAnexo.nrSeqRegraAnexo === +predefinedFileKey)?.nrSeqTipoDocumento ?? '0';
      handledAnexo.nrSeqTipoDocumento = String(nrSeqTipoDocumento);
      return handledAnexo;
    });

    return handledAnexos;
  }
}
